import { safeLocalStorage, safeSessionStorage } from '@zealot/shared/browser';
import { Toaster } from '@zealot/ui';
import { BannedGuard } from '@/components/auth/BannedGuard';
import { CurrentUserInitializer } from '@/components/auth/CurrentUserInitializer';
import { OtpAuthWrapper } from '@/components/auth/OtpAuthWrapper';
import { GlobalSideEffects } from '@/components/GlobalSideEffects';
import { ImpersonationBanner } from '@/components/impersonation/ImpersonationBanner';
import { ApiClientProvider } from '@/contexts/ApiClientContext';
import { ApiWebsocketProvider } from '@/contexts/ApiWebsocketContext';
import { BannedUserProvider, useBannedUser } from '@/contexts/BannedUserContext';
import { BrandProvider } from '@/contexts/brand/BrandContext';
import { useRedirectOnLogin } from '@/hooks/useRedirectOnLogin';
import { useUrlTokenHandler } from '@/hooks/useUrlTokenHandler';
import { captureInviteTag } from '@/lib/appBootstrap/captureInviteTag';
import { handleClearCache } from '@/lib/appBootstrap/handleClearCache';
import { syncBrandFromUrl } from '@/lib/appBootstrap/syncBrandFromUrl';
import { installTranslateGuard } from '@/lib/installTranslateGuard';
import { createQueryClient } from '@/lib/queryClient';
import { AuthProvider } from '@/pages/AuthContext';
import { getHubBrand, useAppStore } from '@/store';
import { subscribeHubScopedCache } from '@/store/subscribeHubScopedCache';
import logger from '@/utils/logger';
import '@/styles/globals.css';
import { Auth0Provider } from '@auth0/auth0-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { initializeBrowser, isBrowserMonitoringEnabled } from '@zealot/monitoring/browser';
import { AnimatePresence } from 'framer-motion';
import { Poppins } from 'next/font/google';
import { useRouter } from 'next/router';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { redactTokenFromAnalytics } from '@/lib/auth/redactTokenFromAnalytics';
import ErrorBoundary from '../components/ErrorBoundary';
import './global.css';

// @zealot/ui resolves `font-poppins` through --font-poppins-var; the name must match.
const poppins = Poppins({
  subsets: ['latin'],
  variable: '--font-poppins-var',
  weight: ['100', '200', '300', '400', '500', '600', '700'],
});

if (typeof window !== 'undefined') {
  // Guard against browser translation extensions detaching nodes from under
  // React (facebook/react#11538) — must run before React hydrates.
  installTranslateGuard();

  const environment = process.env.NEXT_PUBLIC_ENVIRONMENT || 'local';
  const isLocal = environment === 'local';
  const isTest = environment === 'test';

  if (window.location.pathname !== '/email-preferences') {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_API_KEY, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
      // Disable event tracking in local/test, but keep feature flags working
      autocapture: !isLocal && !isTest,
      capture_pageview: !isLocal && !isTest,
      disable_session_recording: isLocal || isTest,
      before_send: redactTokenFromAnalytics,
      // Replay snapshots must reach before_send uncompressed, or the redactor cannot see their DOM.
      session_recording: { compress_events: false },
      loaded: () => {
        if (isLocal) {
          logger.debug('PostHog feature flags enabled in local environment (events disabled)');
        } else if (isTest) {
          logger.debug('PostHog feature flags enabled in test environment (events disabled)');
        }
      },
    });
  }
}

const SessionApp = ({ Component, pageProps }) => {
  const [redirectUri, setRedirectUri] = useState('');
  const updateHubBrand = useAppStore((s) => s.hubBrand.updateHubBrand);
  const router = useRouter();
  const [queryClient] = useState(() => createQueryClient());

  // The hub's consequences belong to the store, not to a hook every hub surface has to remember
  // to mount.
  useEffect(() => subscribeHubScopedCache(queryClient), [queryClient]);

  // Root-level token handler: strips tokens from URL and injects into store if needed
  useUrlTokenHandler();

  // After login, redirect to stored URL if one exists
  useRedirectOnLogin();

  // Clean up stale auth flags on root mount (but preserve auth tokens for persistent sessions)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.pathname !== '/') return;

    // Only clear the in-progress flag — do NOT wipe auth tokens
    safeLocalStorage.removeItem('auth_in_progress');
  }, []);

  useEffect(() => {
    try {
      captureInviteTag(new URL(window.location.href), safeLocalStorage, logger);
    } catch (_e) {
      // no-op
    }
  }, []);

  useEffect(() => {
    setRedirectUri(`${window.location.protocol}//${window.location.hostname}`);

    // Global clearCache handler so it works from any route BEFORE early returns
    try {
      handleClearCache({
        localStorage: safeLocalStorage,
        sessionStorage: safeSessionStorage,
        location: window.location,
        history: window.history,
        redirect: (href) => {
          window.location.href = href;
        },
      });
    } catch (_e) {
      // no-op
    }

    // Associate user with brand as a group if both are available
    const storedBrand = getHubBrand().brandName ?? getHubBrand().hubLabel ?? null;
    if (storedBrand && typeof posthog !== 'undefined') {
      // Simple direct approach to create a group
      try {
        posthog.group('company', storedBrand);
        logger.info('Associated with company group', { brand: storedBrand });
      } catch (_e) {
        // Analytics should never block the app shell from rendering.
      }
    }
  }, []);

  // Keep the hub's label in sync with URL changes (brand param or subdomain)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      syncBrandFromUrl({
        url: new URL(window.location.href),
        hostname: window.location.hostname,
        currentHubLabel: getHubBrand().hubLabel ?? null,
        hubHasIdentity: Boolean(getHubBrand().uuid),
        onHubLabelChanged: (hubLabel) => updateHubBrand({ hubLabel }),
        analytics: typeof posthog !== 'undefined' ? posthog : null,
        logger,
      });
    } catch (_e) {
      // no-op
    }
  }, [router.asPath, updateHubBrand]);

  // Initialize New Relic Browser Agent for Real User Monitoring
  useEffect(() => {
    if (isBrowserMonitoringEnabled()) {
      initializeBrowser({
        accountId: process.env.NEXT_PUBLIC_NEW_RELIC_ACCOUNT_ID,
        applicationId: process.env.NEXT_PUBLIC_NEW_RELIC_APPLICATION_ID,
        licenseKey: process.env.NEXT_PUBLIC_NEW_RELIC_BROWSER_LICENSE_KEY,
      });
    }
  }, []);

  if (!redirectUri) return null;

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary routeKey={router.asPath}>
        <Auth0Provider
          domain={process.env.NEXT_PUBLIC_AUTH0_DOMAIN}
          clientId={process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID}
          useRefreshTokens={true}
          cacheLocation="localstorage"
          authorizationParams={{
            audience: 'https://api.joinzealot.com',
            scope: 'openid profile email api:request offline_access',
            redirect_uri: typeof window !== 'undefined' ? window.location.origin : undefined,
          }}
        >
          <AuthProvider>
            <BannedUserProvider>
              <BannedGuard>
                <ApiClientProvider>
                  <ApiWebsocketProvider>
                    <CurrentUserInitializer />
                    <BrandProvider>
                      <OtpAuthWrapper>
                        <GlobalSideEffects authenticated />
                        <AnimatePresence mode="wait">
                          <main className={`${poppins.variable}`}>
                            <PostHogProvider client={posthog}>
                              <ImpersonationBanner />
                              <Component {...pageProps} />
                            </PostHogProvider>
                            <Toaster position="top-center" />
                          </main>
                        </AnimatePresence>
                      </OtpAuthWrapper>
                    </BrandProvider>
                  </ApiWebsocketProvider>
                </ApiClientProvider>
              </BannedGuard>
            </BannedUserProvider>
          </AuthProvider>
        </Auth0Provider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
};

const App = ({ Component, pageProps }) =>
  Component.isPublic ? <Component {...pageProps} /> : <SessionApp Component={Component} pageProps={pageProps} />;

export default App;

App.propTypes = {
  Component: PropTypes.func.isRequired,
  pageProps: PropTypes.object.isRequired,
};
